import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Plane,
  Clock,
  Eye,
  RefreshCw,
  ChevronDown,
  GanttChart,
  Zap,
  CheckCircle2,
  Circle,
  Loader2,
  XCircle,
  AlertTriangle,
  SkipForward,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import dayjs from 'dayjs';
import { apiGet } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { API_ROUTES, TASK_STATUS_COLORS, FLIGHT_STATUS_COLORS } from '@avgrp/shared';

// ─── Types ──────────────────────────────────────────────────

interface TaskTemplate {
  id: string;
  name: string;
  code: string;
}

interface TaskAssignment {
  id: string;
  user?: { id: string; firstName: string; lastName: string; employeeId: string } | null;
  material?: { id: string; name: string } | null;
}

interface FlightTask {
  id: string;
  name: string;
  description?: string | null;
  phase: string;
  plannedStart: string;
  plannedEnd: string;
  actualStart?: string | null;
  actualEnd?: string | null;
  status: string;
  priority: number;
  taskTemplate?: TaskTemplate | null;
  assignments: TaskAssignment[];
}

interface Aircraft {
  id: string;
  registration: string;
  aircraftType: { name: string; iataCode: string; category: string };
}

interface Airport {
  id: string;
  iataCode: string;
  name: string;
}

interface ApiFlight {
  id: string;
  flightNumber: string;
  direction: string;
  status: string;
  std: string;
  sta: string;
  etd?: string | null;
  eta?: string | null;
  atd?: string | null;
  ata?: string | null;
  gate?: string | null;
  stand?: string | null;
  prepTime?: number;
  postTime?: number;
  aircraft: Aircraft;
  origin: Airport;
  destination: Airport;
  flightTasks: FlightTask[];
}

/** A swimlane groups all flights for one aircraft */
interface AircraftSwimlane {
  key: string;         // unique key (aircraftId or flight-based fallback)
  registration: string;
  aircraftType: string;
  category: string;
  flights: ApiFlight[];
}

type ViewMode = 'all' | 'single' | 'active';

// ─── Constants ──────────────────────────────────────────────

const DEFAULT_HOUR_WIDTH = 120; // px per hour
const ZOOM_LEVELS = [40, 60, 80, 100, 120, 160, 200, 280]; // available zoom levels in px/hour
const FLIGHT_BLOCK_HEIGHT = 32; // height of each flight block in swimlane
const TASK_BAR_HEIGHT = 10; // height of each task bar row
const TASK_ROW_GAP = 2;    // vertical gap between task rows
const MIN_SWIMLANE_HEIGHT = 72; // minimum row height per aircraft
const HEADER_HEIGHT = 48;
const FLIGHT_TOP_PAD = 6;  // top padding for flight block

const ACTIVE_FLIGHT_STATUSES = ['SCHEDULED', 'PREPARING', 'BOARDING', 'DELAYED'];

const TASK_STATUS_ICONS: Record<string, React.ElementType> = {
  PENDING: Circle,
  ASSIGNED: Clock,
  IN_PROGRESS: Loader2,
  COMPLETED: CheckCircle2,
  CANCELLED: XCircle,
  BLOCKED: AlertTriangle,
  SKIPPED: SkipForward,
};

// ─── Helpers ────────────────────────────────────────────────

/** Determine the visible time range: now -120min to now +24h, expanded by flight windows */
function getTimeRange(flights: ApiFlight[]): { start: Date; end: Date } {
  const now = Date.now();
  // Default: now - 2h to now + 24h
  let minTime = now - 120 * 60 * 1000;
  let maxTime = now + 24 * 60 * 60 * 1000;

  for (const flight of flights) {
    const std = new Date(flight.std).getTime();
    const sta = new Date(flight.sta).getTime();
    const isDeparture = flight.direction === 'DEPARTURE';

    // Prep window only for departures
    if (isDeparture) {
      const prepMs = (flight.prepTime ?? 60) * 60 * 1000;
      const prepStart = std - prepMs;
      if (prepStart < minTime) minTime = prepStart;
    }

    if (std < minTime) minTime = std;
    if (sta > maxTime) maxTime = sta;

    // Post-flight time only for arrivals
    if (!isDeparture) {
      const postMs = (flight.postTime ?? 30) * 60 * 1000;
      const postEnd = sta + postMs;
      if (postEnd > maxTime) maxTime = postEnd;
    }

    // ETD may extend beyond STD for delayed flights
    if (flight.etd) {
      const etd = new Date(flight.etd).getTime();
      if (etd > maxTime) maxTime = etd;
    }

    // Consider task times
    for (const task of flight.flightTasks) {
      const ps = new Date(task.plannedStart).getTime();
      const pe = new Date(task.plannedEnd).getTime();
      if (ps < minTime) minTime = ps;
      if (pe > maxTime) maxTime = pe;
    }
  }

  // Round down to nearest hour and add padding
  const start = new Date(minTime);
  start.setMinutes(0, 0, 0);

  const end = new Date(maxTime);
  end.setMinutes(0, 0, 0);
  end.setHours(end.getHours() + 1);

  return { start, end };
}

function timeToX(time: Date, rangeStart: Date, hourWidth: number): number {
  const diffMs = time.getTime() - rangeStart.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  return diffHours * hourWidth;
}

function formatDuration(start: string, end: string): string {
  const diffMs = new Date(end).getTime() - new Date(start).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

/** Compute the delay in minutes between STD and ETD (returns 0 if no delay) */
function getDelayMinutes(flight: ApiFlight): number {
  if (!flight.etd) return 0;
  const diff = new Date(flight.etd).getTime() - new Date(flight.std).getTime();
  return diff > 0 ? Math.round(diff / 60000) : 0;
}

/** Compute turnaround time between two flights using ETD (if delayed) and STD */
function getTurnaroundMinutes(prevFlight: ApiFlight, nextFlight: ApiFlight): number {
  // Post-flight end of arriving flight
  const postMs = (prevFlight.postTime ?? 30) * 60 * 1000;
  const prevPostEnd = new Date(prevFlight.sta).getTime() + postMs;
  // Prep start of departing flight — use ETD if delayed, else STD
  const depTime = nextFlight.etd
    ? new Date(nextFlight.etd).getTime()
    : new Date(nextFlight.std).getTime();
  const prepMs = (nextFlight.prepTime ?? 60) * 60 * 1000;
  const nextPrepStart = depTime - prepMs;
  return Math.round((nextPrepStart - prevPostEnd) / 60000);
}

/** Minimum turnaround thresholds in minutes */
const MIN_TURNAROUND: Record<string, number> = {
  REGIONAL: 25,
  MEDIUM: 35,
  HEAVY: 50,
};

function getTurnaroundAlertLevel(minutes: number, category: string): 'ok' | 'warn' | 'critical' {
  const minTa = MIN_TURNAROUND[category] ?? 35;
  if (minutes < 0) return 'critical';
  if (minutes < minTa) return 'warn';
  return 'ok';
}

/** Get flight block color based on direction */
function getFlightBlockColor(flight: ApiFlight): string {
  const statusColor = FLIGHT_STATUS_COLORS[flight.status] || '#6B7280';
  return statusColor;
}

/**
 * Greedy interval-packing: assign each task to the first row where it does not
 * overlap with any previously placed task.  Returns taskId → rowIndex.
 */
function assignTaskRows(tasks: FlightTask[]): Map<string, number> {
  // Sort by plannedStart so earlier tasks get placed first
  const sorted = [...tasks].sort(
    (a, b) => new Date(a.plannedStart).getTime() - new Date(b.plannedStart).getTime(),
  );
  // rowEnds[i] = the end-time (ms) of the last task placed in row i
  const rowEnds: number[] = [];
  const result = new Map<string, number>();

  for (const task of sorted) {
    const startMs = new Date(task.plannedStart).getTime();
    let placed = false;
    for (let r = 0; r < rowEnds.length; r++) {
      if (startMs >= rowEnds[r]!) {
        rowEnds[r] = new Date(task.plannedEnd).getTime();
        result.set(task.id, r);
        placed = true;
        break;
      }
    }
    if (!placed) {
      result.set(task.id, rowEnds.length);
      rowEnds.push(new Date(task.plannedEnd).getTime());
    }
  }
  return result;
}

// ─── Component ──────────────────────────────────────────────

export function GanttPage() {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [viewMode, setViewMode] = useState<ViewMode>('all');
  const [selectedFlightId, setSelectedFlightId] = useState<string>('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hoveredFlight, setHoveredFlight] = useState<{
    flight: ApiFlight;
    x: number;
    y: number;
  } | null>(null);
  const [hourWidth, setHourWidth] = useState(DEFAULT_HOUR_WIDTH);
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const zoomIn = () => {
    const idx = ZOOM_LEVELS.indexOf(hourWidth);
    if (idx < ZOOM_LEVELS.length - 1) {
      setHourWidth(ZOOM_LEVELS[idx + 1]!);
    }
  };

  const zoomOut = () => {
    const idx = ZOOM_LEVELS.indexOf(hourWidth);
    if (idx > 0) {
      setHourWidth(ZOOM_LEVELS[idx - 1]!);
    }
  };

  const {
    data: flightsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['gantt-flights', selectedDate, user?.baseId],
    queryFn: () =>
      apiGet<{ data: ApiFlight[]; count: number }>(API_ROUTES.FLIGHTS, {
        params: { baseId: user?.baseId, date: selectedDate },
      }),
    select: (res) => res.data,
    enabled: !!user?.baseId,
  });

  const allFlights = flightsData ?? [];

  // Active flights = flights with active statuses
  const activeFlights = useMemo(
    () => allFlights.filter((f) => ACTIVE_FLIGHT_STATUSES.includes(f.status)),
    [allFlights],
  );

  // Hide flights whose STD (and ETD if present) are both more than 1 hour ago
  const isRecentFlight = (f: ApiFlight) => {
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    const std = new Date(f.std).getTime();
    const etd = f.etd ? new Date(f.etd).getTime() : std;
    return std > oneHourAgo || etd > oneHourAgo;
  };

  // The flights to display based on view mode
  const displayFlights = useMemo(() => {
    if (viewMode === 'single') {
      const flight = allFlights.find((f) => f.id === selectedFlightId);
      return flight ? [flight] : [];
    }
    const source = viewMode === 'all' ? allFlights : activeFlights;
    return source.filter(isRecentFlight);
  }, [viewMode, selectedFlightId, allFlights, activeFlights]);

  // ─── Group flights into aircraft swimlanes ────────────────
  // Uses aircraft.id as the grouping key so flights on different physical
  // aircraft never collapse into the same row (even if registrations match).
  // For "UNKNOWN" aircraft, each flight gets its own lane to prevent stacking.
  const swimlanes = useMemo(() => {
    const laneMap = new Map<string, AircraftSwimlane>();
    for (const flight of displayFlights) {
      const reg = flight.aircraft?.registration || '';
      const isUnknown = !reg || reg === 'UNKNOWN';
      // Use aircraft ID as key; for UNKNOWN aircraft give each flight its own lane
      const key = isUnknown ? `unknown-${flight.id}` : flight.aircraft.id;
      const displayReg = isUnknown ? flight.flightNumber : reg;

      if (!laneMap.has(key)) {
        laneMap.set(key, {
          key,
          registration: displayReg,
          aircraftType: flight.aircraft?.aircraftType?.name || 'Unknown',
          category: flight.aircraft?.aircraftType?.category || 'MEDIUM',
          flights: [],
        });
      }
      laneMap.get(key)!.flights.push(flight);
    }
    // Sort flights within each lane by STD
    for (const lane of laneMap.values()) {
      lane.flights.sort((a, b) => new Date(a.std).getTime() - new Date(b.std).getTime());
    }
    // Sort lanes by earliest STD
    return Array.from(laneMap.values()).sort((a, b) => {
      const aMin = Math.min(...a.flights.map(f => new Date(f.std).getTime()));
      const bMin = Math.min(...b.flights.map(f => new Date(f.std).getTime()));
      return aMin - bMin;
    });
  }, [displayFlights]);

  // Compute time axis range
  const timeRange = useMemo(() => getTimeRange(displayFlights), [displayFlights]);
  const totalHours = (timeRange.end.getTime() - timeRange.start.getTime()) / (1000 * 60 * 60);
  const totalWidth = totalHours * hourWidth;

  // ─── Multiline task layout ──────────────────────────────────
  // For each flight, assign tasks to rows so overlapping tasks stack vertically
  const taskRowMap = useMemo(() => {
    const map = new Map<string, Map<string, number>>(); // flightId → (taskId → rowIndex)
    for (const lane of swimlanes) {
      for (const flight of lane.flights) {
        if (flight.flightTasks.length > 0) {
          map.set(flight.id, assignTaskRows(flight.flightTasks));
        }
      }
    }
    return map;
  }, [swimlanes]);

  // Compute per-lane heights based on the max task rows across all flights in each lane
  const laneHeights = useMemo(() => {
    return swimlanes.map((lane) => {
      let maxRows = 0;
      for (const flight of lane.flights) {
        const rows = taskRowMap.get(flight.id);
        if (rows) {
          const maxRow = Math.max(...Array.from(rows.values())) + 1;
          if (maxRow > maxRows) maxRows = maxRow;
        }
      }
      // height = top pad + flight block + gap + (task rows) + bottom pad
      const taskAreaHeight = maxRows > 0
        ? 4 + maxRows * TASK_BAR_HEIGHT + (maxRows - 1) * TASK_ROW_GAP + 4
        : 0;
      return Math.max(MIN_SWIMLANE_HEIGHT, FLIGHT_TOP_PAD + FLIGHT_BLOCK_HEIGHT + taskAreaHeight + FLIGHT_TOP_PAD);
    });
  }, [swimlanes, taskRowMap]);

  const totalBodyHeight = laneHeights.reduce((sum, h) => sum + h, 0);

  // Generate hour markers
  const hourMarkers = useMemo(() => {
    const markers: Date[] = [];
    const current = new Date(timeRange.start);
    while (current <= timeRange.end) {
      markers.push(new Date(current));
      current.setHours(current.getHours() + 1);
    }
    return markers;
  }, [timeRange]);

  // Auto-scroll to "now" on first load
  useEffect(() => {
    if (scrollRef.current && displayFlights.length > 0) {
      const now = new Date();
      const nowX = timeToX(now, timeRange.start, hourWidth);
      const containerWidth = scrollRef.current.clientWidth;
      scrollRef.current.scrollLeft = Math.max(0, nowX - containerWidth / 3);
    }
  }, [displayFlights.length, timeRange.start]);

  // Auto-select first flight in single mode
  useEffect(() => {
    if (viewMode === 'single' && !selectedFlightId && allFlights.length > 0) {
      setSelectedFlightId(allFlights[0]!.id);
    }
  }, [viewMode, selectedFlightId, allFlights]);

  // Current time indicator position
  const nowX = timeToX(new Date(), timeRange.start, hourWidth);
  const showNowLine = nowX >= 0 && nowX <= totalWidth;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Flight Gantt</h1>
          <p className="mt-1 text-sm text-gray-500">
            Aircraft timeline — each row shows one aircraft&apos;s flights
          </p>
        </div>
        <button onClick={() => refetch()} className="btn-secondary">
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Controls bar */}
      <div className="card flex flex-wrap items-center gap-4">
        {/* Date picker */}
        <div>
          <label htmlFor="gantt-date" className="sr-only">Date</label>
          <input
            id="gantt-date"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="input-field"
          />
        </div>

        {/* View mode toggle */}
        <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
          <button
            onClick={() => setViewMode('all')}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              viewMode === 'all'
                ? 'bg-white text-aviation-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Plane className="h-3.5 w-3.5" />
            All Flights
          </button>
          <button
            onClick={() => setViewMode('active')}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              viewMode === 'active'
                ? 'bg-white text-aviation-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Zap className="h-3.5 w-3.5" />
            Active
          </button>
          <button
            onClick={() => setViewMode('single')}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              viewMode === 'single'
                ? 'bg-white text-aviation-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Eye className="h-3.5 w-3.5" />
            Single
          </button>
        </div>

        {/* Flight selector (single mode) */}
        {viewMode === 'single' && (
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="input-field flex w-64 items-center justify-between"
            >
              <span className="truncate">
                {selectedFlightId
                  ? allFlights.find((f) => f.id === selectedFlightId)?.flightNumber ?? 'Select flight'
                  : 'Select flight'}
              </span>
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </button>
            {dropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setDropdownOpen(false)}
                />
                <div className="absolute left-0 top-full z-20 mt-1 max-h-72 w-80 overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
                  {allFlights.map((flight) => {
                    const statusColor = FLIGHT_STATUS_COLORS[flight.status] || '#6B7280';
                    const delayMin = getDelayMinutes(flight);
                    return (
                      <button
                        key={flight.id}
                        onClick={() => {
                          setSelectedFlightId(flight.id);
                          setDropdownOpen(false);
                        }}
                        className={`flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 ${
                          selectedFlightId === flight.id ? 'bg-aviation-50' : ''
                        }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-semibold">
                              {flight.flightNumber}
                            </span>
                            <span
                              className="badge text-xs"
                              style={{
                                backgroundColor: `${statusColor}15`,
                                color: statusColor,
                              }}
                            >
                              {flight.status.replace('_', ' ')}
                            </span>
                            {delayMin > 0 && (
                              <span className="rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-600">
                                +{delayMin}m
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 text-xs text-gray-400">
                            {flight.origin.iataCode} → {flight.destination.iataCode}
                            {' · '}
                            {flight.aircraft.registration}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  {allFlights.length === 0 && (
                    <p className="px-3 py-4 text-center text-sm text-gray-400">
                      No flights for this date
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Zoom controls */}
        <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-0.5">
          <button
            onClick={zoomOut}
            disabled={hourWidth <= ZOOM_LEVELS[0]!}
            className="rounded-md p-1.5 text-gray-500 hover:bg-white hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30 transition-colors"
            title="Zoom out"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <span className="min-w-[3rem] text-center text-xs font-medium text-gray-500">
            {Math.round((hourWidth / DEFAULT_HOUR_WIDTH) * 100)}%
          </span>
          <button
            onClick={zoomIn}
            disabled={hourWidth >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]!}
            className="rounded-md p-1.5 text-gray-500 hover:bg-white hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30 transition-colors"
            title="Zoom in"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
        </div>

        {/* Stats */}
        <div className="ml-auto flex items-center gap-4 text-sm text-gray-500">
          <span>
            <GanttChart className="mr-1 inline h-4 w-4" />
            {swimlanes.length} aircraft
          </span>
          <span>{displayFlights.length} flights</span>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-5 rounded-sm bg-blue-500/80" />
          <span className="text-gray-600">Departure</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-5 rounded-sm bg-emerald-500/80" />
          <span className="text-gray-600">Arrival</span>
        </div>
        <div className="mx-1 h-4 border-l border-gray-300" />
        <div className="flex items-center gap-1.5">
          <div
            className="h-3 w-5 rounded-sm"
            style={{
              background: 'repeating-linear-gradient(-45deg, transparent, transparent 2px, rgba(59,130,246,0.12) 2px, rgba(59,130,246,0.12) 4px)',
              border: '1px dashed rgba(59,130,246,0.35)',
            }}
          />
          <span className="text-gray-600">Dep. prep</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div
            className="h-3 w-5 rounded-sm"
            style={{
              background: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(16,185,129,0.08) 3px, rgba(16,185,129,0.08) 6px)',
              border: '1px dashed rgba(16,185,129,0.4)',
            }}
          />
          <span className="text-gray-600">Arr. handling</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div
            className="h-3 w-5 rounded-sm"
            style={{
              background: 'rgba(168,85,247,0.15)',
              border: '1px dashed rgba(168,85,247,0.4)',
            }}
          />
          <span className="text-gray-600">Turnaround</span>
        </div>
        <div className="mx-1 h-4 border-l border-gray-300" />
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-5 rounded-sm" style={{ backgroundColor: '#9CA3AF90', borderLeft: '2px solid #9CA3AF' }} />
          <span className="text-gray-600">Task</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-5 rounded-sm" style={{ backgroundColor: '#10B98190', borderLeft: '2px solid #10B981' }} />
          <span className="text-gray-600">Done</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-5 rounded-sm" style={{ backgroundColor: '#3B82F690', borderLeft: '2px solid #3B82F6' }} />
          <span className="text-gray-600">In progress</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-full w-0.5 border-l-2 border-dashed border-red-400" style={{ height: 12 }} />
          <span className="text-gray-600">Current time</span>
        </div>
      </div>

      {/* Gantt chart */}
      {isLoading ? (
        <div className="card flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-aviation-200 border-t-aviation-600" />
        </div>
      ) : isError ? (
        <div className="card flex flex-col items-center justify-center py-20">
          <AlertTriangle className="mb-3 h-12 w-12 text-red-300" />
          <p className="text-sm font-medium text-red-600">Failed to load flights</p>
          <p className="mt-1 text-xs text-gray-400">{(error as any)?.message || 'An unexpected error occurred'}</p>
          <button onClick={() => refetch()} className="btn-secondary mt-4">
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </button>
        </div>
      ) : swimlanes.length === 0 ? (
        <div className="card flex flex-col items-center justify-center py-20">
          <GanttChart className="mb-3 h-12 w-12 text-gray-300" />
          <p className="text-sm text-gray-500">
            {viewMode === 'single' && !selectedFlightId
              ? 'Select a flight to view its timeline'
              : viewMode === 'active'
                ? 'No active flights for this date'
                : 'No flights for this date'}
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden p-0" ref={containerRef}>
          <div className="flex">
            {/* ── Left panel: aircraft labels ── */}
            <div className="flex-shrink-0 border-r border-gray-200 bg-gray-50" style={{ width: 220 }}>
              {/* Header spacer */}
              <div
                className="flex items-center border-b border-gray-200 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500"
                style={{ height: HEADER_HEIGHT }}
              >
                Aircraft
              </div>
              {/* Aircraft rows */}
              {swimlanes.map((lane, laneIdx) => (
                <div
                  key={lane.key}
                  className="flex items-center gap-2 border-b border-gray-100 px-4"
                  style={{ height: laneHeights[laneIdx] }}
                >
                  <Plane className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate font-mono text-sm font-bold text-gray-900">
                        {lane.registration}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-[11px] text-gray-400">
                      <span>{lane.aircraftType}</span>
                      <span className="mx-0.5">·</span>
                      <span>{lane.flights.length} flight{lane.flights.length !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Right panel: timeline ── */}
            <div className="flex-1 overflow-x-auto" ref={scrollRef}>
              <div style={{ width: totalWidth, minWidth: '100%' }} className="relative">
                {/* Time axis header */}
                <div
                  className="sticky top-0 z-10 flex border-b border-gray-200 bg-white"
                  style={{ height: HEADER_HEIGHT }}
                >
                  {hourMarkers.map((hour, i) => {
                    const x = timeToX(hour, timeRange.start, hourWidth);
                    return (
                      <div
                        key={i}
                        className="absolute flex items-center border-l border-gray-200 pl-2 text-xs text-gray-500"
                        style={{ left: x, height: HEADER_HEIGHT }}
                      >
                        <span className="font-mono font-medium">
                          {dayjs(hour).format('HH:mm')}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* ── Aircraft swimlane rows ── */}
                {swimlanes.map((lane, laneIdx) => {
                  const laneH = laneHeights[laneIdx]!;
                  return (
                    <div
                      key={lane.key}
                      className="relative border-b border-gray-100"
                      style={{ height: laneH }}
                    >
                      {/* Grid lines */}
                      {hourMarkers.map((hour, i) => {
                        const x = timeToX(hour, timeRange.start, hourWidth);
                        return (
                          <div
                            key={i}
                            className="absolute top-0 h-full border-l border-gray-100"
                            style={{ left: x }}
                          />
                        );
                      })}

                      {/* ── Turnaround gaps between consecutive flights ── */}
                      {lane.flights.map((flight, fIdx) => {
                        if (fIdx === 0) return null;
                        const prevFlight = lane.flights[fIdx - 1]!;
                        const postMs = (prevFlight.postTime ?? 30) * 60 * 1000;
                        const prevPostEnd = new Date(prevFlight.sta).getTime() + postMs;
                        const nextPrepMs = (flight.prepTime ?? 60) * 60 * 1000;
                        // Use ETD if delayed
                        const depTime = flight.etd
                          ? new Date(flight.etd).getTime()
                          : new Date(flight.std).getTime();
                        const nextPrepStart = depTime - nextPrepMs;

                        const gapMinutes = Math.round((nextPrepStart - prevPostEnd) / 60000);

                        // Show turnaround block even with negative gap (overlap = critical)
                        const gapStartX = timeToX(new Date(Math.min(prevPostEnd, nextPrepStart)), timeRange.start, hourWidth);
                        const gapEndX = timeToX(new Date(Math.max(prevPostEnd, nextPrepStart)), timeRange.start, hourWidth);
                        const gapWidth = gapEndX - gapStartX;

                        if (gapWidth < 4) return null;

                        const alertLevel = getTurnaroundAlertLevel(gapMinutes, lane.category);
                        const alertStyles = {
                          ok: {
                            bg: 'rgba(168,85,247,0.06)',
                            border: 'rgba(168,85,247,0.3)',
                            text: 'text-purple-400/80',
                          },
                          warn: {
                            bg: 'rgba(245,158,11,0.12)',
                            border: 'rgba(245,158,11,0.5)',
                            text: 'text-amber-600',
                          },
                          critical: {
                            bg: 'rgba(239,68,68,0.12)',
                            border: 'rgba(239,68,68,0.5)',
                            text: 'text-red-600',
                          },
                        }[alertLevel];

                        return (
                          <div
                            key={`ta-${fIdx}`}
                            className="absolute flex items-center justify-center"
                            title={`Turnaround: ${gapMinutes}m${alertLevel === 'warn' ? ' ⚠ Tight' : alertLevel === 'critical' ? ' ❌ Overlap/Critical' : ''}`}
                            style={{
                              left: gapStartX,
                              width: gapWidth,
                              top: 4,
                              height: laneH - 8,
                              background: alertStyles.bg,
                              borderLeft: `1px dashed ${alertStyles.border}`,
                              borderRight: `1px dashed ${alertStyles.border}`,
                              borderRadius: 4,
                            }}
                          >
                            {gapWidth > 30 && (
                              <span className={`select-none text-[9px] font-bold ${alertStyles.text}`}>
                                {gapMinutes}m
                                {alertLevel !== 'ok' && ' ⚠'}
                              </span>
                            )}
                          </div>
                        );
                      })}

                      {/* ── Flight blocks ── */}
                      {lane.flights.map((flight) => {
                        const stdTime = new Date(flight.std);
                        const staTime = new Date(flight.sta);
                        const prepMinutes = flight.prepTime ?? 60;
                        const postMinutes = flight.postTime ?? 30;
                        const prepStartTime = new Date(stdTime.getTime() - prepMinutes * 60 * 1000);
                        const postEndTime = new Date(staTime.getTime() + postMinutes * 60 * 1000);
                        const delayMin = getDelayMinutes(flight);

                        const prepX = timeToX(prepStartTime, timeRange.start, hourWidth);
                        const stdX = timeToX(stdTime, timeRange.start, hourWidth);
                        const staX = timeToX(staTime, timeRange.start, hourWidth);
                        const postX = timeToX(postEndTime, timeRange.start, hourWidth);
                        const prepWidth = stdX - prepX;
                        const flightWidth = staX - stdX;
                        const postWidth = postX - staX;

                        const isDeparture = flight.direction === 'DEPARTURE';
                        const blockColor = isDeparture ? '#3B82F6' : '#10B981';
                        const statusColor = FLIGHT_STATUS_COLORS[flight.status] || '#6B7280';
                        const blockTop = FLIGHT_TOP_PAD;

                        return (
                          <div key={flight.id}>
                            {/* Prep window (only for departures — arrivals don't need prep) */}
                            {isDeparture && prepMinutes > 0 && prepWidth > 0 && (
                              <div
                                className="absolute rounded-l-md"
                                style={{
                                  left: prepX,
                                  width: prepWidth,
                                  top: blockTop + 2,
                                  height: FLIGHT_BLOCK_HEIGHT - 4,
                                  background: `repeating-linear-gradient(-45deg, transparent, transparent 3px, ${blockColor}0D 3px, ${blockColor}0D 6px)`,
                                  borderLeft: `1.5px dashed ${blockColor}50`,
                                  borderTop: `1px dashed ${blockColor}30`,
                                  borderBottom: `1px dashed ${blockColor}30`,
                                }}
                              />
                            )}

                            {/* Main flight block (STD → STA) */}
                            <div
                              className="group absolute z-10 flex cursor-pointer items-center overflow-hidden rounded-md border shadow-sm transition-all hover:shadow-md hover:brightness-95"
                              style={{
                                left: stdX,
                                width: Math.max(flightWidth, 20),
                                top: blockTop,
                                height: FLIGHT_BLOCK_HEIGHT,
                                backgroundColor: `${blockColor}25`,
                                borderColor: `${blockColor}60`,
                                borderWidth: 1.5,
                              }}
                              onMouseEnter={(e) => {
                                const rect = containerRef.current?.getBoundingClientRect();
                                if (rect) {
                                  setHoveredFlight({
                                    flight,
                                    x: e.clientX - rect.left,
                                    y: e.clientY - rect.top,
                                  });
                                }
                              }}
                              onMouseLeave={() => setHoveredFlight(null)}
                            >
                              <div className="relative z-10 flex items-center gap-1 truncate px-2">
                                <Plane
                                  className="h-3 w-3 flex-shrink-0"
                                  style={{ color: blockColor, transform: isDeparture ? 'rotate(-45deg)' : 'rotate(45deg)' }}
                                />
                                <span className="truncate text-xs font-bold" style={{ color: blockColor }}>
                                  {flight.flightNumber}
                                </span>
                                {flightWidth > 100 && (
                                  <span className="truncate text-[10px] font-medium text-gray-500">
                                    {flight.origin.iataCode}→{flight.destination.iataCode}
                                  </span>
                                )}
                                {flightWidth > 160 && (
                                  <span
                                    className="ml-auto flex-shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold"
                                    style={{
                                      backgroundColor: `${statusColor}20`,
                                      color: statusColor,
                                    }}
                                  >
                                    {flight.status.replace('_', ' ')}
                                  </span>
                                )}
                              </div>

                              {/* Time labels at edges */}
                              {flightWidth > 70 && (
                                <>
                                  <span className="absolute bottom-0.5 left-1.5 text-[8px] font-medium text-gray-400">
                                    {dayjs(flight.std).format('HH:mm')}
                                  </span>
                                  <span className="absolute bottom-0.5 right-1.5 text-[8px] font-medium text-gray-400">
                                    {dayjs(flight.sta).format('HH:mm')}
                                  </span>
                                </>
                              )}
                            </div>

                            {/* Post-flight zone (only for arrivals — departures leave) */}
                            {!isDeparture && postMinutes > 0 && postWidth > 0 && (
                              <div
                                className="absolute rounded-r-md"
                                style={{
                                  left: staX,
                                  width: postWidth,
                                  top: blockTop + 2,
                                  height: FLIGHT_BLOCK_HEIGHT - 4,
                                  background: `repeating-linear-gradient(45deg, transparent, transparent 3px, ${blockColor}08 3px, ${blockColor}08 6px)`,
                                  borderRight: `1.5px dashed ${blockColor}40`,
                                  borderTop: `1px dashed ${blockColor}20`,
                                  borderBottom: `1px dashed ${blockColor}20`,
                                }}
                              />
                            )}

                            {/* Delay indicator */}
                            {delayMin > 0 && flight.etd && (() => {
                              const etdX = timeToX(new Date(flight.etd!), timeRange.start, hourWidth);
                              return (
                                <div
                                  className="absolute z-[5]"
                                  style={{
                                    left: etdX,
                                    top: blockTop,
                                    height: FLIGHT_BLOCK_HEIGHT,
                                    borderLeft: '2px dashed rgba(239,68,68,0.5)',
                                  }}
                                >
                                  <div className="absolute -top-3 left-1 select-none whitespace-nowrap rounded bg-red-50 px-1 text-[8px] font-bold text-red-500">
                                    +{delayMin}m
                                  </div>
                                </div>
                              );
                            })()}

                            {/* ── Task bars (multiline) ── */}
                            {flight.flightTasks.length > 0 && (() => {
                              const flightRows = taskRowMap.get(flight.id);
                              const taskBaseTop = blockTop + FLIGHT_BLOCK_HEIGHT + 4;
                              const taskStatusColors: Record<string, string> = {
                                PENDING: '#9CA3AF',
                                ASSIGNED: '#F59E0B',
                                IN_PROGRESS: '#3B82F6',
                                COMPLETED: '#10B981',
                                CANCELLED: '#EF4444',
                                BLOCKED: '#EF4444',
                                SKIPPED: '#6B7280',
                              };
                              return flight.flightTasks.map((task) => {
                                const rowIndex = flightRows?.get(task.id) ?? 0;
                                const tStart = new Date(task.plannedStart);
                                const tEnd = new Date(task.plannedEnd);
                                const tX = timeToX(tStart, timeRange.start, hourWidth);
                                const tW = Math.max(timeToX(tEnd, timeRange.start, hourWidth) - tX, 4);
                                const tColor = taskStatusColors[task.status] || '#9CA3AF';
                                const code = task.taskTemplate?.code || task.name;
                                const taskTop = taskBaseTop + rowIndex * (TASK_BAR_HEIGHT + TASK_ROW_GAP);
                                return (
                                  <div
                                    key={task.id}
                                    className="absolute z-[6] rounded-sm"
                                    title={`${code} — ${task.status} (${dayjs(task.plannedStart).format('HH:mm')}–${dayjs(task.plannedEnd).format('HH:mm')})`}
                                    style={{
                                      left: tX,
                                      width: tW,
                                      top: taskTop,
                                      height: TASK_BAR_HEIGHT,
                                      backgroundColor: `${tColor}90`,
                                      borderLeft: `2px solid ${tColor}`,
                                    }}
                                  >
                                    {tW > 40 && (
                                      <span
                                        className="absolute inset-0 flex items-center truncate px-1 text-[7px] font-bold leading-none text-white"
                                        style={{ textShadow: '0 0 2px rgba(0,0,0,0.3)' }}
                                      >
                                        {code}
                                      </span>
                                    )}
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}

                {/* Current time line */}
                {showNowLine && (
                  <div
                    className="absolute top-0 z-20 w-0.5 bg-red-400"
                    style={{
                      left: nowX,
                      height: HEADER_HEIGHT + totalBodyHeight,
                    }}
                  >
                    <div className="absolute -left-1.5 -top-0.5 h-3 w-3 rounded-full bg-red-400" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tooltip */}
          {hoveredFlight && (
            <div
              className="pointer-events-none absolute z-50 w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-xl"
              style={{
                left: Math.min(
                  hoveredFlight.x + 12,
                  (containerRef.current?.clientWidth ?? 600) - 300,
                ),
                top: hoveredFlight.y + 12,
              }}
            >
              <div className="mb-2 flex items-center justify-between">
                <h4 className="font-semibold text-gray-900">
                  {hoveredFlight.flight.flightNumber}
                </h4>
                <span
                  className="badge text-xs"
                  style={{
                    backgroundColor: `${FLIGHT_STATUS_COLORS[hoveredFlight.flight.status]}15`,
                    color: FLIGHT_STATUS_COLORS[hoveredFlight.flight.status],
                  }}
                >
                  {hoveredFlight.flight.status.replace('_', ' ')}
                </span>
              </div>

              <div className="space-y-1.5 text-xs text-gray-500">
                <div className="flex justify-between">
                  <span>Route</span>
                  <span className="font-mono font-medium text-gray-700">
                    {hoveredFlight.flight.origin.iataCode} → {hoveredFlight.flight.destination.iataCode}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Direction</span>
                  <span className="font-medium text-gray-700">
                    {hoveredFlight.flight.direction}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Aircraft</span>
                  <span className="font-mono text-gray-700">
                    {hoveredFlight.flight.aircraft.registration} ({hoveredFlight.flight.aircraft.aircraftType.name})
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>STD</span>
                  <span className="font-mono text-gray-700">
                    {dayjs(hoveredFlight.flight.std).format('HH:mm')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>STA</span>
                  <span className="font-mono text-gray-700">
                    {dayjs(hoveredFlight.flight.sta).format('HH:mm')}
                  </span>
                </div>
                {hoveredFlight.flight.etd && (
                  <div className="flex justify-between">
                    <span>ETD</span>
                    <span className="font-mono font-bold text-red-600">
                      {dayjs(hoveredFlight.flight.etd).format('HH:mm')}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Prep Window</span>
                  <span className="font-medium text-blue-600">
                    {hoveredFlight.flight.prepTime ?? 60}m before STD
                  </span>
                </div>
                {hoveredFlight.flight.gate && (
                  <div className="flex justify-between">
                    <span>Gate</span>
                    <span className="font-medium text-gray-700">{hoveredFlight.flight.gate}</span>
                  </div>
                )}
                {hoveredFlight.flight.stand && (
                  <div className="flex justify-between">
                    <span>Stand</span>
                    <span className="font-medium text-gray-700">{hoveredFlight.flight.stand}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Tasks</span>
                  <span className="font-medium text-gray-700">
                    {hoveredFlight.flight.flightTasks.filter(t => t.status === 'COMPLETED').length}/
                    {hoveredFlight.flight.flightTasks.length} completed
                  </span>
                </div>
                {getDelayMinutes(hoveredFlight.flight) > 0 && (
                  <div className="flex justify-between">
                    <span>Delay</span>
                    <span className="font-bold text-red-600">
                      +{getDelayMinutes(hoveredFlight.flight)}m
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
